import { type ReactElement, type ReactNode } from 'react';
import { Box, Text } from '@/ui';
import { ProjectBadge } from './ProjectBadge';
import {
  projectIdentityBadgeSlotSx,
  projectIdentityFallbackIconSlotSx,
  projectIdentityPrimaryTextSx,
  projectIdentityRootSx,
  projectIdentitySecondaryTextSx,
  projectIdentityTextFrameSx,
} from './ProjectIdentity.styles';
import type { ProjectBadgeSize } from './ProjectBadge.styles';
import type { ProjectDto } from '@flowqa/shared';

export type ProjectIdentityProject = Pick<
  ProjectDto,
  'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'
>;

type ProjectIdentityProps = {
  badgeSize?: ProjectBadgeSize;
  disabled?: boolean;
  fallbackIcon?: ReactNode;
  fallbackName?: string;
  project: ProjectIdentityProject | null;
  secondaryText?: string | null;
};

export const ProjectIdentity = ({
  badgeSize = 'compact',
  disabled = false,
  fallbackIcon,
  fallbackName = '-',
  project,
  secondaryText = null,
}: ProjectIdentityProps): ReactElement => {
  const projectName = project?.name ?? fallbackName;
  const projectBadge =
    project?.badgeEnabled === true ? (
      <ProjectBadge
        chars={project.badgeChars}
        color={project.badgeColor}
        disabled={disabled}
        size={badgeSize}
      />
    ) : null;
  const leadingContent =
    projectBadge === null && fallbackIcon ? (
      <Box component="span" sx={projectIdentityFallbackIconSlotSx}>
        {fallbackIcon}
      </Box>
    ) : (
      projectBadge
    );

  return (
    <Box component="span" sx={projectIdentityRootSx}>
      {leadingContent === null ? null : (
        <Box component="span" sx={projectIdentityBadgeSlotSx}>
          {leadingContent}
        </Box>
      )}
      <Box component="span" sx={projectIdentityTextFrameSx}>
        <Text role="bodyStrong" component="span" sx={projectIdentityPrimaryTextSx}>
          {projectName}
        </Text>
        {secondaryText ? (
          <Text
            role="caption"
            color="textSecondary"
            component="span"
            sx={projectIdentitySecondaryTextSx}
          >
            {secondaryText}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};
